import React, { useEffect, useRef, useState } from 'react';
import { DateRangePicker, DateRangePickerOptions } from 'utils/DateRangePicker';
import moment from 'moment';
import { useField, useFormikContext } from 'formik';
import 'bootstrap-daterangepicker/daterangepicker.css';
import 'styles/components/datepicker.css';
import { useIsMobile } from 'hooks/useIsMobile';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface MinthrDateRangePickerProps {
  label?: string;
  className?: string;
  inline?: boolean;
  linkedCalendars?: boolean;
  singleDatePicker?: boolean;
  autoApply?: boolean;
  autoUpdateInput?: boolean;
  alwaysShowCalendars?: boolean;
  showDropdowns?: boolean;
  isInvalidDate?: (date: moment.Moment) => boolean;
  onChange?: (start: moment.Moment, end: moment.Moment) => void;
  onClick?: (datePicker: DateRangePicker) => void;
  onHoverDateChange?: (date: moment.Moment | null) => void;
  hoverStartDate?: moment.Moment | null;
  hoverEndDate?: moment.Moment | null;
  onClear?: () => void;
  ranges?: { [key: string]: [moment.Moment, moment.Moment] } | undefined;
  selectionMode?: 'date' | 'month' | 'year' | 'week' | 'range';
  withSelectionTypes?: boolean;
  showTwoCalendars?: boolean;
  openInClass?: string;
  classInlineContainer?: string;
  customView?: (
    startDate: moment.Moment | null,
    endDate: moment.Moment | null,
    selectionMode: 'date' | 'month' | 'year' | 'week' | 'range',
  ) => React.ReactNode;
  startDate?: moment.Moment | null;
  endDate?: moment.Moment | null;
  minDate?: moment.Moment | null;
  maxDate?: moment.Moment | null;
  startDateName?: string;
  endDateName?: string;
  saveFormat?: string;
  disabled?: boolean;
  maxSpan?: moment.Duration | null;
  withClearBtn?: boolean;
  customViewClassName?: string;
  displayFormat?: string;
  DayRangePicker?: boolean;
  withoutLabel?: boolean;
  t?: TFunction<['translation', ...string[]], undefined>;
}

interface HandelFormikValueProps {
  startDateName: string;
  endDateName?: string;
  rangeDate: [moment.Moment, moment.Moment | null] | null;
  saveFormat?: string;
  DatePicker: DateRangePicker | null;
  setRangeDate: (
    rangeDate: [moment.Moment, moment.Moment | null] | null,
  ) => void;
  clearDate: number;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  DayRangePicker: boolean;
}
const parseFieldDate = (value: unknown, format?: string): moment.Moment => {
  if (moment.isMoment(value) || value instanceof Date) {
    return moment(value);
  }
  if (typeof value === 'string') {
    const strict = moment(value, format ?? 'DD/MM/YYYY', true);
    return strict.isValid() ? strict : moment(value);
  }
  return moment(value as moment.MomentInput, format ?? 'DD/MM/YYYY');
};

const HandelFormikValue = ({
  startDateName,
  endDateName,
  saveFormat,
  DatePicker,
  setRangeDate,
  clearDate,
  setErrorMessage,
  DayRangePicker,
}: HandelFormikValueProps) => {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(startDateName);
  const [fieldEnd, metaEnd] = useField(endDateName ?? startDateName);
  const [isSetInitDate, setIsSetInitDate] = useState(
    field.value ? true : false,
  );

  const [isSetInitEndDate, setIsSetInitEndDate] = useState(
    fieldEnd.value ? true : false,
  );

  useEffect(() => {
    if (DatePicker?.startDate && isSetInitDate) {
      if (DayRangePicker) {
        const dayString = DatePicker.startDate.date().toString();
        setFieldValue(startDateName, dayString);
      } else {
        setFieldValue(
          startDateName,
          DatePicker?.startDate.format(saveFormat ?? 'DD/MM/YYYY'),
        );
      }
    }
    if (DatePicker?.endDate && endDateName && isSetInitEndDate) {
      if (DayRangePicker) {
        const dayString = DatePicker.endDate.date().toString();
        setFieldValue(endDateName, dayString);
      } else {
        setFieldValue(
          endDateName,
          DatePicker.endDate.format(saveFormat ?? 'DD/MM/YYYY'),
        );
      }
    }
    setIsSetInitDate(true);
    setIsSetInitEndDate(true);
  }, [DatePicker?.startDate, DatePicker?.endDate]);

  useEffect(() => {
    if (field.value && DatePicker) {
      if (DayRangePicker) {
        const dayString = field.value;
        if (dayString >= 1 && dayString <= 31) {
          DatePicker.startDate = moment().date(dayString);
          DatePicker.updateView();
          DatePicker.updateElement();
          setRangeDate([
            dayString,
            DatePicker.endDate ? DatePicker.endDate.date() : null,
          ]);
        }
      } else {
        DatePicker.startDate = parseFieldDate(field.value, saveFormat);
        DatePicker.updateView();
        DatePicker.updateElement();
        setRangeDate([DatePicker.startDate, DatePicker.endDate]);
      }
    }
  }, []);

  useEffect(() => {
    if (fieldEnd.value && DatePicker) {
      if (DayRangePicker) {
        const dayNum = parseInt(fieldEnd.value);
        if (dayNum >= 1 && dayNum <= 31) {
          DatePicker.endDate = moment().date(dayNum);
          DatePicker.updateView();
          DatePicker.updateElement();
          setRangeDate([DatePicker.startDate.date(), dayNum]);
        }
      } else {
        DatePicker.endDate = parseFieldDate(fieldEnd.value, saveFormat);
        DatePicker.updateView();
        DatePicker.updateElement();
        setRangeDate([DatePicker.startDate, DatePicker.endDate]);
      }
    }
  }, []);

  useEffect(() => {
    if (clearDate > 0) {
      setFieldValue(startDateName, null);
      if (endDateName) setFieldValue(endDateName, null);
    }
  }, [clearDate]);

  // useEffect(() => {
  //   if (field.value && DatePicker) {
  //     if (DatePicker.startDate.format(saveFormat ?? 'L') == field.value) {
  //       DatePicker.startDate = moment(field.value, saveFormat ?? 'L');
  //       if (DatePicker.singleDatePicker) {
  //         DatePicker.endDate = DatePicker.startDate;
  //       }
  //       DatePicker.updateView();
  //       DatePicker.updateElement();
  //       setRangeDate([DatePicker.startDate, DatePicker.endDate]);
  //     }
  //   }
  // }, [field]);

  // useEffect(() => {
  //   if (field.value && DatePicker) {
  //     if (DatePicker.endDate.format(saveFormat ?? 'L') == fieldEnd.value) {
  //       DatePicker.endDate = moment(fieldEnd.value, saveFormat ?? 'L');

  //       DatePicker.updateView();
  //       DatePicker.updateElement();
  //       setRangeDate([DatePicker.startDate, DatePicker.endDate]);
  //     }
  //   }
  // }, [fieldEnd]);

  useEffect(() => {
    if ((meta.error && meta.touched) || (metaEnd.error && metaEnd.touched)) {
      setErrorMessage(meta.error ?? metaEnd.error ?? '');
    } else {
      setErrorMessage('');
    }
  }, [meta.error, meta.touched, metaEnd.error, metaEnd.touched]);

  return null;
};

const MinthrDateRangePicker: React.FC<MinthrDateRangePickerProps> = ({
  label,
  className,
  inline,
  linkedCalendars = true,
  singleDatePicker,
  autoApply = true,
  autoUpdateInput = true,
  alwaysShowCalendars = true,
  showDropdowns = true,
  isInvalidDate,
  onChange,
  onClick,
  onHoverDateChange,
  onClear,
  ranges,
  selectionMode = 'date',
  withSelectionTypes = false,
  openInClass,
  classInlineContainer,
  customView,
  startDate,
  endDate,
  minDate,
  maxDate,
  startDateName,
  endDateName,
  saveFormat,
  disabled,
  showTwoCalendars = true,
  maxSpan = null,
  withClearBtn = false,
  customViewClassName,
  displayFormat = 'DD/MM/YYYY',
  DayRangePicker = false,
  withoutLabel = false,
  hoverStartDate = null,
  hoverEndDate = null,
}) => {
  const { t } = useTranslation();
  const datePickerRef = useRef<HTMLInputElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const labelRef = useRef<HTMLLabelElement>(null);
  const [DatePicker, setDatePicker] = useState<DateRangePicker | null>(null);
  const [rangeDate, setRangeDate] = useState<[any, any] | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [clearDate, setClearDate] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const isMobile = useIsMobile();
  const handleClearDate = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (datePickerRef.current) {
      datePickerRef.current.value = '';
      datePickerRef.current.classList.remove('active');
    }
    setRangeDate(null);
    setClearDate((v) => v + 1);
    onClear?.();
  };

  useEffect(() => {
    if (datePickerRef.current) {
      const options: DateRangePickerOptions = {
        DayRangePicker: DayRangePicker,
        autoApply: isMobile ? false : true,
        autoUpdateInput: true,
        alwaysShowCalendars: true,
        showDropdowns: true,
        inline: inline,
        parentEl: inline ? datePickerRef.current : document.body,
        linkedCalendars: linkedCalendars,
        singleDatePicker: singleDatePicker,
        isInvalidDate: isInvalidDate,
        ranges: ranges,
        minDate: moment.isMoment(minDate) ? minDate : undefined,
        maxDate: moment.isMoment(maxDate) ? maxDate : undefined,
        selectionMode: selectionMode,
        showTwoCalendars: showTwoCalendars,
        withSelectionTypes: withSelectionTypes,
        startDate: DayRangePicker
          ? ''
          : moment.isMoment(startDate)
            ? startDate
            : moment().startOf('day'),
        maxSpan: maxSpan,
        endDate: DayRangePicker
          ? ''
          : singleDatePicker
            ? moment.isMoment(startDate)
              ? startDate
              : moment().startOf('day')
            : moment.isMoment(endDate)
              ? endDate
              : moment().endOf('day'),
        disabled: disabled,
        displayFormat: displayFormat,
        onChange: (start, end) => {
          if (DayRangePicker) {
            const startDay = start?.date();
            const endDay = end?.date();
            if (singleDatePicker) {
              onChange?.(startDay, startDay);
            } else {
              onChange?.(startDay, endDay);
            }
          } else {
            if (singleDatePicker) {
              onChange?.(start, start);
            } else {
              onChange?.(start, end);
            }
          }
        },
        onHoverDateChange: (date) => {
          if (DayRangePicker) {
            onHoverDateChange?.(date?.date() ?? null);
          } else {
            onHoverDateChange?.(date ?? null);
          }
        },
        hoverStartDate: hoverStartDate,
        hoverEndDate: hoverEndDate,
        locale: {
          applyLabel: t('common.apply', 'Appliquer'),
          cancelLabel: t('common.cancel', 'Annuler'),
        },
        t: t,
      };

      const datePicker = new DateRangePicker(datePickerRef.current, options);

      if (singleDatePicker) {
        datePicker.endDate = datePicker.startDate;
        datePicker.updateView();
      }

      setDatePicker(datePicker);

      if (startDate !== null && startDate !== undefined) {
        datePicker.updateElement();
        if (DayRangePicker) {
          const startDay =
            typeof startDate === 'string'
              ? startDate
              : datePicker.startDate.date();
          const endDay = singleDatePicker
            ? startDay
            : typeof endDate === 'string'
              ? endDate
              : datePicker.endDate.date();
          setRangeDate([startDay, endDay]);
        } else {
          const s = moment.isMoment(startDate)
            ? startDate
            : moment().startOf('day');
          const e = singleDatePicker
            ? s
            : moment.isMoment(endDate)
              ? endDate
              : null;
          setRangeDate([s, e] as [moment.Moment, moment.Moment | null]);
        }
      }

      datePicker.callback = (start, end) => {
        if (DayRangePicker) {
          const startDay = start.date();
          const endDay = end.date();
          if (datePicker.singleDatePicker && start) {
            setRangeDate([startDay, startDay]);
          } else {
            setRangeDate([startDay, endDay]);
          }
        } else {
          if (datePicker.singleDatePicker && start) {
            setRangeDate([start, start]);
          } else {
            setRangeDate([start, end || null]);
          }
        }
      };

      return () => {
        datePicker.destroy();
      };
    }
  }, [datePickerRef]);

  useEffect(() => {
    if (DatePicker && startDate !== null && startDate !== undefined) {
      if (DayRangePicker) {
        const startDay =
          typeof startDate === 'string' ? startDate : moment().date();
        const endDay =
          typeof endDate === 'string'
            ? endDate
            : singleDatePicker
              ? startDay
              : moment().date();

        DatePicker.startDate = moment().date(startDay);
        DatePicker.endDate = moment().date(endDay);
        setRangeDate([startDay, endDay]);
      } else {
        const s = startDate;
        const e = moment.isMoment(endDate) ? endDate : s;
        DatePicker.startDate = s;
        DatePicker.endDate = e;
        DatePicker.updateView();
        DatePicker.updateElement();
        setRangeDate([s, e]);
      }
    }
  }, [startDate, endDate, DatePicker, DayRangePicker, singleDatePicker]);
  useEffect(() => {
    if (DatePicker && startDate === null && endDate === null) {
      DatePicker.setStartDate(moment().startOf('day'));
      DatePicker.setEndDate(moment().endOf('day'));
      DatePicker.updateView();
      DatePicker.updateElement();
      setRangeDate(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.disabled = disabled ?? false;
    }
  }, [disabled]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.isInvalidDate = isInvalidDate ?? (() => false);
    }
  }, [isInvalidDate]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.onClick = onClick;
    }
  }, [onClick, DatePicker]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.onChange = (start, end) => {
        if (DayRangePicker) {
          const startDay = start.date();
          const endDay = end.date();
          if (DatePicker.singleDatePicker) {
            onChange?.(startDay, startDay);
          } else {
            onChange?.(startDay, endDay);
          }
        } else {
          if (DatePicker.singleDatePicker) {
            onChange?.(start, start);
          } else {
            onChange?.(start, end);
          }
        }
      };
    }
  }, [onChange, DayRangePicker, DatePicker]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.selectionMode = selectionMode;
      DatePicker.updateView();
      DatePicker.updateElement();
    }
  }, [selectionMode]);
  useEffect(() => {
    if (DatePicker) {
      DatePicker.maxSpan = maxSpan;
      DatePicker.selectionMode = selectionMode;
      DatePicker.updateCalendars();
      DatePicker.updateElement();
    }
  }, [maxSpan]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.onHoverDateChange = (date) => {
        if (DayRangePicker) {
          onHoverDateChange?.(date?.date() ?? null);
        } else {
          onHoverDateChange?.(date ?? null);
        }
      };
    }
  }, [onHoverDateChange, DayRangePicker]);

  useEffect(() => {
    if (DatePicker) {
      DatePicker.hoverStartDate = hoverStartDate;
      DatePicker.hoverEndDate = hoverEndDate;
      DatePicker.updateCalendars();
    }
  }, [hoverStartDate, hoverEndDate, DatePicker]);

  useEffect(() => {
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth);
    }
  }, []);
  useEffect(() => {
    if (DayRangePicker && datePickerRef.current) {
      const start = rangeDate?.[0];
      const end = rangeDate?.[1];

      if (singleDatePicker) {
        if (start) {
          datePickerRef.current.value = String(start);
        } else {
          datePickerRef.current.value = '';
        }
      } else {
        if (start && end) {
          datePickerRef.current.value = `${start} - ${end}`;
        } else if (start) {
          datePickerRef.current.value = String(start);
        } else {
          datePickerRef.current.value = '';
        }
      }
    }
  }, [DayRangePicker, rangeDate, singleDatePicker]);

  useEffect(() => {
    if (DayRangePicker && DatePicker) {
      const HIDE_CLASS = 'daterangepicker-hidden-element';

      const hideHeader = () => {
        const container = DatePicker.container;
        if (container) {
          const elementsToHide = [
            container.querySelector('.calendar-table thead'),
            container.querySelector('.monthselect'),
            container.querySelector('.yearselect'),
            container.querySelector('.daterangepicker .drp-calendar.right'),
          ];

          elementsToHide.forEach((element) => {
            if (element) {
              element.classList.add(HIDE_CLASS);
            }
          });
        }
      };

      hideHeader();

      // Patch the library function for persistence
      const originalUpdateView = DatePicker.updateView.bind(DatePicker);
      DatePicker.updateView = function () {
        originalUpdateView();
        hideHeader();
      };

      return () => {
        DatePicker.updateView = originalUpdateView;
      };
    }
  }, [DayRangePicker, DatePicker]);

  return (
    <>
      {startDateName && DatePicker && (
        <HandelFormikValue
          DatePicker={DatePicker}
          startDateName={startDateName}
          endDateName={endDateName}
          rangeDate={rangeDate}
          saveFormat={saveFormat}
          setRangeDate={setRangeDate}
          clearDate={clearDate}
          setErrorMessage={setErrorMessage}
          DayRangePicker={DayRangePicker}
        />
      )}
      {inline ? (
        <div
          className={`minthr-inline-date-picker ${classInlineContainer}`}
          ref={datePickerRef}
        ></div>
      ) : customView ? (
        <>
          <div ref={datePickerRef} className={customViewClassName}>
            {customView(
              rangeDate?.[0] || null,
              rangeDate?.[1] || null,
              DatePicker?.selectionMode || selectionMode,
            )}
          </div>
          {errorMessage ? (
            <div className="minthr-form-message">
              <span className="field-validation-error">
                <span>{errorMessage}</span>
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <div
          className={`minthr-form-outline ${errorMessage ? 'error' : ''}   ${className}`}
        >
          <div
            className={`minthr-form-outline ${isHovering && withClearBtn && DatePicker?.startDate ? 'clear-date' : ''}`}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <input
              className={'minthr-form-control '}
              type="text"
              ref={datePickerRef}
              disabled={disabled}
              readOnly={isMobile}
            />
            <a className="clear-click" onClick={handleClearDate}></a>
            <label ref={labelRef} className="minthr-form-label">
              {label}
            </label>
            <>
              {errorMessage ? (
                <div className="minthr-form-message">
                  <span className="field-validation-error">
                    <span>{errorMessage}</span>
                  </span>
                </div>
              ) : null}
            </>
            <div className="minthr-form-notch">
              <div className="minthr-form-notch-leading"></div>
              <div
                className="minthr-form-notch-middle"
                style={{ minWidth: `${!withoutLabel ? labelWidth + 20 : 0}px` }}
              ></div>
              <div className="minthr-form-notch-trailing"></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MinthrDateRangePicker;
